import _ from "lodash";
import {isDefined} from "@/helper/Value";

export const OPEN_FAN_KEY = 'characteristic-fan';

const DEFAULT__GROUP_BY_LETTER = {enabled: true};

const store = {
	namespaced: true,
	state: {
		show: false,
		groupByLetter: DEFAULT__GROUP_BY_LETTER,
		key: undefined,
		title: undefined,
		subTitle: undefined,
		titleFormats: undefined,
		withKeyword: false,
		keyword: '',
		searchConfig: undefined,
		characteristicType: undefined,
		characteristicItems: undefined,
		characteristicMetaProperty: undefined,
		selected: undefined,
		selectedBeforeParentOpened: {},
		nothingAllowed: undefined,
		supplementsConfig: undefined,
		withExperience: false,
		withSelfAssessment: false,
		withEducation: false,
		withRequirement: false,
		experienceConfig: undefined,
		singleChoice: false,
		sourceCharacteristic: false,
		labelType: undefined,
		labelSet: undefined,
		iconPath: undefined,
		descriptionConfig: undefined,
		showMultipleHint: false,
		keywordPlaceholder: undefined,
		headerInfo: undefined,
		completionCheckConfig: undefined,
		additionsConfig: undefined,
		selectedItemsConfig: undefined,
		maxLevel: undefined,
		parents: [],
		parentOnCloseCallbacks: {},
		onChange: undefined,
		onClose: undefined,
	},
	actions: {
		show ({commit}, data)
		{
			commit('layout/addOpenFan', OPEN_FAN_KEY, {root: true});

			const groupByLetter = isDefined(data.groupByLetter)
				? data.groupByLetter
				: DEFAULT__GROUP_BY_LETTER;

			commit('setKey', data.key);
			commit('setCharacteristicType', data.characteristicType);
			commit('setCharacteristicItems', data.characteristicItems);
			commit('setCharacteristicMetaProperty', data.characteristicMetaProperty);
			commit('setTitle', data.title);
			commit('setSubTitle', data.subTitle);
			commit('setTitleFormats', data.titleFormats);
			commit('setWithKeyword', data.withKeyword || false);
			commit('setKeyword', data.keyword || '');
			commit('setSearchConfig', data.searchConfig);
			commit('setSelected', _.cloneDeep(data.selected));
			commit('setSelectedBeforeParentOpened', {});
			commit('setNothingAllowed', data.nothingAllowed);
			commit('setSupplementsConfig', data.supplementsConfig);
			commit('setWithExperience', data.withExperience || false);
			commit('setWithSelfAssessment', data.withSelfAssessment || false);
			commit('setWithEducation', data.withEducation || false);
			commit('setWithRequirement', data.withRequirement || false);
			commit('setExperienceConfig', data.experienceConfig);
			commit('setSingleChoice', data.singleChoice || false);
			commit('setSourceCharacteristic', data.sourceCharacteristic);
			commit('setGroupByLetter', groupByLetter);
			commit('setLabelType', data.labelType);
			commit('setLabelSet', data.labelSet);
			commit('setIconPath', data.iconPath);
			commit('setDescriptionConfig', data.descriptionConfig);
			commit('setShowMultipleHint', data.showMultipleHint || false);
			commit('setKeywordPlaceholder', data.keywordPlaceholder);
			commit('setHeaderInfo', data.headerInfo);
			commit('setCompletionCheckConfig', data.completionCheckConfig);
			commit('setAdditionsConfig', data.additionsConfig);
			commit('setSelectedItemsConfig', data.selectedItemsConfig);
			commit('setMaxLevel', data.maxLevel);
			commit('setOnChange', data.onChange);
			commit('setOnClose', data.onClose);

			if (data.addAsParent)
			{
				commit('addParent', data.addAsParent);
			}

			commit('setShow', true);
		},
		close ({commit, state}, data)
		{
			commit('setShow', false);

			if (state.onChange && data.confirmed)
			{
				state.onChange({
					selected: state.selected
				});
			}

			if (state.onClose)
			{
				state.onClose({
					skipped: data.skipped
				});
			}

			commit('setKey', undefined);
			commit('setCharacteristicType', undefined);
			commit('setCharacteristicItems', undefined);
			commit('setCharacteristicMetaProperty', undefined);
			commit('setTitle', undefined);
			commit('setSubTitle', undefined);
			commit('setTitleFormats', undefined);
			commit('setWithKeyword', false);
			commit('setKeyword', '');
			commit('setSearchConfig', undefined);
			commit('resetSelected');
			commit('setSelectedBeforeParentOpened', {});
			commit('setParents', []);
			commit('setNothingAllowed', undefined);
			commit('setSupplementsConfig', undefined);
			commit('setWithExperience', false);
			commit('setWithSelfAssessment', false);
			commit('setWithEducation', false);
			commit('setWithRequirement', false);
			commit('setExperienceConfig', undefined);
			commit('setSingleChoice', false);
			commit('setSourceCharacteristic', undefined);
			commit('setGroupByLetter', DEFAULT__GROUP_BY_LETTER);
			commit('setLabelType', undefined);
			commit('setLabelSet', undefined);
			commit('setIconPath', undefined);
			commit('setDescriptionConfig', undefined);
			commit('setShowMultipleHint', false);
			commit('setKeywordPlaceholder', undefined);
			commit('setHeaderInfo', undefined);
			commit('setCompletionCheckConfig', undefined);
			commit('setAdditionsConfig', undefined);
			commit('setSelectedItemsConfig', undefined);
			commit('setMaxLevel', undefined);
			commit('setOnChange', undefined);
			commit('setOnClose', undefined);

			commit('layout/removeOpenFan', OPEN_FAN_KEY, {root: true});
		},
		setPropertyValueForSelectionItem: ({state, commit}, {characteristic, property, value}) =>
		{
			let selectedItemKey = _.findKey(state.selected, item => item.item.id === characteristic.id);

			if (selectedItemKey >= 0)
			{
				state.selected[selectedItemKey][property] = value;
			}

			commit('setSelected', _.cloneDeep(state.selected));
		},
		addParent: ({state, commit}, {characteristic, onClose}) =>
		{
			if (!state.parents)
			{
				state.parents = [];
			}

			state.parentOnCloseCallbacks[characteristic.id] = onClose;
			state.selectedBeforeParentOpened[characteristic.id] = _.cloneDeep(state.selected);
			state.parents.push(characteristic);

			commit('layout/addOpenFan', 'characteristic-fan-parent-' + characteristic.id, {root: true});
		},
		removeParent: ({state, commit}, {characteristic, confirmed}) =>
		{
			if (!confirmed)
			{
				state.selected = state.selectedBeforeParentOpened[characteristic.id];
			}

			state.parents = _.filter(state.parents, parent => parent.id !== characteristic.id);

			const onCloseCallback = state.parentOnCloseCallbacks[characteristic.id];

			if (onCloseCallback)
			{
				onCloseCallback();
			}

			let parentOnCloseCallbacks = state.parentOnCloseCallbacks;
			delete parentOnCloseCallbacks[characteristic.id];
			state.parentOnCloseCallbacks = parentOnCloseCallbacks;

			commit('layout/removeOpenFan', 'characteristic-fan-parent-' + characteristic.id, {root: true});
		},
	},
	mutations: {
		setShow: (state, value) => state.show = value,
		setKey: (state, value) => state.key = value,
		setTitle: (state, value) => state.title = value,
		setSubTitle: (state, value) => state.subTitle = value,
		setTitleFormats: (state, value) => state.titleFormats = value,
		setWithKeyword: (state, value) => state.withKeyword = value,
		setKeyword: (state, value) => state.keyword = value,
		setSearchConfig: (state, value) => state.searchConfig = value,
		setCharacteristicType: (state, value) => state.characteristicType = value,
		setCharacteristicItems: (state, value) => state.characteristicItems = value,
		setCharacteristicMetaProperty: (state, value) => state.characteristicMetaProperty = value,
		resetSelected: (state) => state.selected = undefined,
		setSelected: (state, value) => state.selected = value,
		setSelectedBeforeParentOpened: (state, value) => state.selectedBeforeParentOpened = value,
		setNothingAllowed: (state, value) => state.nothingAllowed = value,
		setSupplementsConfig: (state, value) => state.supplementsConfig = value,
		setWithExperience: (state, value) => state.withExperience = value,
		setWithSelfAssessment: (state, value) => state.withSelfAssessment = value,
		setWithEducation: (state, value) => state.withEducation = value,
		setWithRequirement: (state, value) => state.withRequirement = value,
		setExperienceConfig: (state, value) => state.experienceConfig = value,
		setSingleChoice: (state, value) => state.singleChoice = value,
		setSourceCharacteristic: (state, value) => state.sourceCharacteristic = value,
		setGroupByLetter: (state, value) => state.groupByLetter = value,
		setLabelType: (state, value) => state.labelType = value,
		setLabelSet: (state, value) => state.labelSet = value,
		setIconPath: (state, value) => state.iconPath = value,
		setDescriptionConfig: (state, value) => state.descriptionConfig = value,
		setParents: (state, value) => state.parents = value,
		setShowMultipleHint: (state, value) => state.showMultipleHint = value,
		setKeywordPlaceholder: (state, value) => state.keywordPlaceholder = value,
		setHeaderInfo: (state, value) => state.headerInfo = value,
		setCompletionCheckConfig: (state, value) => state.completionCheckConfig = value,
		setAdditionsConfig: (state, value) => state.additionsConfig = value,
		setSelectedItemsConfig: (state, value) => state.selectedItemsConfig = value,
		setMaxLevel: (state, value) => state.maxLevel = value,
		setOnChange: (state, value) => state.onChange = value,
		setOnClose: (state, value) => state.onClose = value,
	},
};

export default store;
